import classNames from 'classnames';
import { omit } from 'lodash';
import debounce from 'lodash/debounce';
import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CancelIcon, SearchIcon } from '../icons';
import { Icon } from './Icon';

export interface SearchFieldsProps {
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  isLoading?: boolean;
  plain?: boolean;
  iconOnPlain?: boolean;
  allowClear?: boolean;
  onChange?: (value: string) => void;
  inputPops?: React.InputHTMLAttributes<HTMLInputElement>;
  icon?: string;
  searchTimeout?: number;
  value?: string;
}

interface SearchFieldRef {
  onClear: () => void;
  input: HTMLInputElement | null;
}

export const SearchField = forwardRef<SearchFieldRef, SearchFieldsProps>(
  (
    {
      icon,
      plain,
      onChange,
      className,
      inputPops,
      iconOnPlain,
      placeholder,
      searchTimeout = 500,
      allowClear = false,
      isLoading = false,
      disabled = false,
      value,
    },
    ref,
  ) => {
    const [currValue, setCurrValue] = useState(value || '');
    const inputRef = useRef<HTMLInputElement | null>(null); // Internal ref for the input
    const isNeedToShowClearButton = !isLoading && allowClear && !!value;

    const handleDebouncedChange = useMemo(() => {
      return debounce(
        (e: ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value),
        searchTimeout,
      );
    }, [onChange, searchTimeout]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        setCurrValue(e.target.value);
        handleDebouncedChange(e);
      },
      [handleDebouncedChange],
    );

    const baseInput = (
      <input
        ref={inputRef}
        type="text"
        value={currValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={classNames(
          'text-sm flex-1 outline-none !w-full placeholder-gray-400',
          { 'text-black': !className?.includes('text-') },
          className,
        )}
        {...omit(inputPops, ['onClick'])}
      />
    );

    const handleClear = useCallback(() => {
      onChange?.('');
      setCurrValue('');
    }, [onChange]);

    useImperativeHandle(ref, () => ({ onClear: handleClear, input: inputRef.current }), [
      handleClear,
    ]);

    useEffect(() => {
      setCurrValue(value ?? '');
    }, [value]);

    if (plain) {
      return (
        <div className="flex gap-1 h-full" onClick={inputPops?.onClick}>
          {iconOnPlain && (
            <img className="mr-2" src={icon || SearchIcon} alt="Search Icon" width={24} />
          )}
          {baseInput}
          <button
            onClick={e => {
              e.stopPropagation();
              handleClear();
            }}
            className={classNames({ hidden: !isNeedToShowClearButton })}
          >
            <Icon Source={CancelIcon} color="gray" />
          </button>
        </div>
      );
    }

    return (
      <div
        onClick={inputPops?.onClick}
        className={classNames(
          'flex items-center p-3 rounded-md h-10 border select-none',
          {
            'w-full': !className?.includes('w-'),
            'hover:border-gray-900': !disabled && !className?.includes('hover:border-'),
          },
          className,
        )}
      >
        <img className="mr-2" src={icon || SearchIcon} alt="Search Icon" />
        {baseInput}
        {isNeedToShowClearButton && (
          <button
            onClick={e => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <Icon Source={CancelIcon} color="gray" />
          </button>
        )}
        {isLoading && (
          <div className="flex gap-1">
            <div className="w-1 h-1 rounded-lg bg-[rgb(204,_204,_204)] animate-pulse"></div>
            <div className="w-1 h-1 rounded-lg bg-[rgb(204,_204,_204)] animate-pulse"></div>
            <div className="w-1 h-1 rounded-lg bg-[rgb(204,_204,_204)] animate-pulse"></div>
          </div>
        )}
      </div>
    );
  },
);
